import { AgGridAngular } from '@ag-grid-community/angular';
import {
  CellValueChangedEvent,
  ColumnState,
  GridApi,
  GridReadyEvent,
} from '@ag-grid-community/core';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { QueryClient } from '@tanstack/angular-query-experimental';
import { cloneDeep } from 'lodash-es';
import { firstValueFrom } from 'rxjs';

import { VISIT_COST_OPTION } from '@features/patient-group-dropdown/constants/visit-cost-option.const';

import { injectPatientInvoiceables } from '@models/patient-protocol/api/patient-invoiceables.api';
import { injectBatchRemovePatientProtocols } from '@models/patient-protocol/api/patient-protocol.api';
import { injectPatientVisits } from '@models/patient-protocol/api/patient-visits.api';
import { injectPatientProtocolSubTypes } from '@models/patient-protocol/patient-protocol.api';
import { injectSiteBudgetSiteInvoiceables } from '@models/sites/api/site-invoiceables.api';
import { injectSitePaymentSchedulesBySiteId } from '@models/sites/api/sites.api';
import { SiteCostType } from '@models/sites/site-cost.enum';
import { SiteModel } from '@models/sites/sites.store';

import {
  ConfirmationActionModalComponent,
  ConfirmationActionModalData,
} from '@shared/components/modals/confirmation-action-modal/confirmation-action-modal.components';
import { TablePanelComponent } from '@shared/components/table-panel/table-panel.components';
import { SkeletonLoaderDirective } from '@shared/directives/skeleton-loader.directive';
import {
  Currency,
  listPatientInvoiceablesQuery,
  listPatientVisitsQuery,
  listSiteInvoiceablesQuery,
  listSitePaymentSchedulesQuery,
  PatientProtocolType,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import {
  validateRequiredField,
  validateUniqueField,
} from '@shared/utils/ag-validations/ag-validations';
import { Maybe, Utils } from '@shared/utils/utils';

import { siteBudgetConfig } from './grid-config/site-budget-config';
import {
  GridRowData,
  PatientVisitRowData,
  SiteInvoiceableRowData,
} from './types/site-budget-grid-row';

type PatientData =
  | listPatientInvoiceablesQuery
  | listPatientVisitsQuery
  | listSiteInvoiceablesQuery;

@Component({
  imports: [AgGridAngular, TablePanelComponent, SkeletonLoaderDirective],
  selector: 'aux-site-budget-grid',
  styles: [
    `
      ::ng-deep .site-budget-table {
        --checkbox-checked-color: var(--aux-blue);
        --ag-odd-row-background-color: transparent;
        --ag-selected-row-background-color: transparent;
        --ag-checkbox-checked-color: var(--checkbox-checked-color);
        --ag-checkbox-indeterminate-color: var(--checkbox-checked-color);

        .ag-checkbox-input-wrapper:focus-within {
          box-shadow: none;
        }
      }
    `,
  ],
  template: `
    <aux-table-panel>
      <ng-content />
      <ag-grid-angular
        class="site-budget-table new-flat-theme h-[500px] w-full"
        auxSkeletonLoader
        [loading]="isGridLoading() || isDataProcessing()"
        [rowData]="gridData()"
        [gridOptions]="gridOptions()"
        [enableFillHandle]="editMode()"
        (gridReady)="onGridReady($event)"
        (cellEditingStopped)="onCellEditingStopped()"
        (cellValueChanged)="onCellValueChanged($event)"
      />
    </aux-table-panel>
  `,
})
export class SiteBudgetGridComponent {
  queryClient = inject(QueryClient);

  private overlayService = inject(OverlayService);

  deleteSiteInvoiceablesMutation = injectBatchRemovePatientProtocols();
  patientProtocolSubTypes = injectPatientProtocolSubTypes();

  selectedCostType = input.required<SiteCostType>();
  selectedProtocolVersion = input.required<Maybe<string>>();

  siteBudgetPatientInvoiceables = injectPatientInvoiceables({
    enabled: computed(
      () =>
        this.selectedCostType() === SiteCostType.PATIENT_INVOICEABLES &&
        !!this.selectedProtocolVersion()
    ),
    protocolVersionId: this.selectedProtocolVersion,
  });
  siteBudgetPatientVisits = injectPatientVisits({
    enabled: computed(
      () =>
        this.selectedCostType() === SiteCostType.PATIENT_VISITS && !!this.selectedProtocolVersion()
    ),
    protocolVersionId: this.selectedProtocolVersion,
  });

  isSiteInvoiceablesSelected = computed(
    () => this.selectedCostType() === SiteCostType.SITE_INVOICEABLES
  );

  selectedBudgetVersion = input.required<Maybe<string>>();
  selectedSiteId = input.required<Maybe<string>>();

  siteBudgetSiteInvoiceables = injectSiteBudgetSiteInvoiceables({
    budgetVersionId: this.selectedBudgetVersion,
    enabled: computed(
      () =>
        this.isSiteInvoiceablesSelected() &&
        !!this.selectedProtocolVersion() &&
        !!this.selectedSiteId()
    ),
    protocolVersionId: this.selectedProtocolVersion,
    siteId: this.selectedSiteId,
  });
  sitePaymentSchedules = injectSitePaymentSchedulesBySiteId({
    costType: this.selectedCostType,
    protocolVersion: this.selectedProtocolVersion,
    siteId: this.selectedSiteId,
  });

  gridAPI = signal<GridApi<GridRowData> | null>(null);
  gridData = signal<GridRowData[]>([]);
  protocolsByCostType = signal<PatientData[]>([]);
  selectedRows = signal<GridRowData[]>([]);

  site = input.required<SiteModel | undefined>();

  currency = computed(() => {
    return this.gridData().length
      ? this.gridData()[0].currency
      : (this.site()?.currency ?? Currency.USD);
  });

  disabled = input(false);
  editMode = input.required<boolean>();

  goToEditMode = output<void>();

  gridOptions = computed(() => {
    const currency = this.currency();
    const editMode = this.editMode();
    const isSiteInvoiceables = this.isSiteInvoiceablesSelected();
    const disabled = this.disabled();

    return untracked(() => {
      return siteBudgetConfig({
        addRow: () => this.addRows(1),
        currency,
        disabled,
        editMode,
        goToEditMode: () => this.goToEditMode.emit(),
        isSiteInvoiceables,
        setFirstEditableCell: () => this.setFirstEditableCell(),
        setSelectedRows: (row, isSelected) => this.setSelectedRows(row, isSelected),
      });
    });
  });
  isGridLoading = computed(
    () =>
      this.sitePaymentSchedules.isFetching() ||
      this.siteBudgetSiteInvoiceables.isFetching() ||
      this.siteBudgetPatientInvoiceables.isFetching() ||
      this.deleteSiteInvoiceablesMutation.isPending()
  );
  siteInvoiceablesSubTypeId = computed(() => {
    const subTypes = this.patientProtocolSubTypes.data()?.data || [];

    return subTypes?.find(
      ({ patient_protocol_type }) =>
        patient_protocol_type === PatientProtocolType.PATIENT_PROTOCOL_OVERHEAD
    )?.id;
  });

  isDataProcessing = input(false);
  selectedPatientGroup = input.required<Maybe<string>>();

  _syncGridOptions = effect(() => {
    // any changes to the grid options will trigger a re-render
    const newGridOptions = this.gridOptions();

    // don't track the gridAPI
    const gridAPI = untracked(() => this.gridAPI());

    if (!gridAPI) {
      return;
    }

    Object.entries(newGridOptions).forEach(([key, value]) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      gridAPI.setGridOption(key as any, value);
    });
  });
  initialData: GridRowData[] = [];
  protocolsByCostTypeEffect = effect(() => {
    const selectedCostType = this.selectedCostType();

    const patientVisitsData = this.siteBudgetPatientVisits.data();
    const siteInvoiceablesData = this.siteBudgetSiteInvoiceables.data();
    const patientInvoiceablesData = this.siteBudgetPatientInvoiceables.data();
    const selectedPatientGroup = this.selectedPatientGroup();

    const patientVisitsByPatientGroup = (patientVisitsData || []).filter(({ patient_group_id }) => {
      return selectedPatientGroup === VISIT_COST_OPTION.value
        ? patient_group_id === null
        : patient_group_id === selectedPatientGroup;
    });

    const mapper = new Map<SiteCostType, PatientData[]>([
      [SiteCostType.PATIENT_INVOICEABLES, patientInvoiceablesData || []],
      [SiteCostType.PATIENT_VISITS, patientVisitsByPatientGroup || []],
      [SiteCostType.SITE_INVOICEABLES, siteInvoiceablesData || []],
    ]);

    const protocols = mapper.get(selectedCostType) || [];

    const paymentsBySiteBudgetId = (this.sitePaymentSchedules.data() || []).filter(
      ({ site_budget_version_id }) => site_budget_version_id === this.selectedBudgetVersion()
    );

    this.gridData.set(this.parseProtocols(protocols, paymentsBySiteBudgetId));
  });

  private readonly defaultColumnState: ColumnState[] = [
    { colId: 'name', sort: 'asc' },
    { colId: 'frequency', sort: 'asc' },
  ];

  siteInvoiceablesColumnState = this.defaultColumnState;

  addRows(numberRows: number) {
    const newRows: GridRowData[] = Array.from({ length: numberRows }, () => ({
      amount: 0,
      currency: this.currency(),
      frequency: null,
      id: Utils.uuid(),
      name: '',
      note: '',
      optional: false,
      paymentScheduleId: null,
    }));

    this.gridAPI()?.applyTransactionAsync({ add: newRows });
  }

  applyColumnSortState() {
    if (this.isSiteInvoiceablesSelected()) {
      this.siteInvoiceablesColumnState = this.defaultColumnState;
      this.gridAPI()?.applyColumnState({
        state: this.siteInvoiceablesColumnState,
      });
    } else {
      // Reset column state for non-site invoiceables
      this.gridAPI()?.applyColumnState({
        defaultState: { sort: null },
      });
    }
  }

  cancelChanges() {
    this.gridAPI()?.setGridOption('rowData', cloneDeep(this.initialData));
    this.clearSelection();
  }

  clearSelection() {
    this.selectedRows.set([]);
    this.gridAPI()?.deselectAll();
  }

  constructor() {
    effect(() => {
      this.initialData = cloneDeep(this.gridData().map((row) => ({ ...row, errors: {} })));
    });

    effect(() => {
      if (this.selectedCostType() && this.gridAPI()) {
        this.applyColumnSortState();
      }
    });

    effect(() => {
      const _ = this.editMode();
      const DELAY = 100;

      if (!(this.isGridLoading() || this.isDataProcessing())) {
        setTimeout(() => {
          this.gridAPI()!.sizeColumnsToFit();
        }, DELAY);
      }
    });
  }

  getColumnState() {
    return this.gridAPI()?.getColumnState();
  }

  getGridData() {
    const data: GridRowData[] = [];

    this.gridAPI()?.forEachNodeAfterFilter((node) => {
      if (node.data) {
        data.push(node.data);
      }
    });

    return data;
  }

  onCellEditingStopped() {
    this.validateGridData();
  }

  onCellValueChanged(e: CellValueChangedEvent<GridRowData>) {
    if (e.source === 'rangeService') {
      this.validateGridData();
    }
  }

  onGridReady(event: GridReadyEvent) {
    this.gridAPI.set(event.api);
    this.gridAPI()?.sizeColumnsToFit();
  }

  parseProtocols(
    protocols: PatientData[],
    paymentsBySiteBudgetId: listSitePaymentSchedulesQuery[]
  ) {
    return (
      protocols.map((protocol) => {
        const payment = paymentsBySiteBudgetId.find(
          ({ patient_protocol }) => patient_protocol.id === protocol.id
        );

        if (this.isSiteInvoiceablesSelected()) {
          const siteInvoiceables = protocol as listSiteInvoiceablesQuery;
          const currency = this.site()?.currency ?? Currency.USD;

          return {
            amount: siteInvoiceables.amount,
            currency,
            frequency: siteInvoiceables.frequency,
            id: siteInvoiceables.id,
            name: siteInvoiceables.name,
            note: siteInvoiceables.note || '',
            paymentScheduleId: siteInvoiceables.id || null,
          } as SiteInvoiceableRowData;
        }

        return {
          amount: payment?.expense_amount?.amount || 0,
          currency: (payment?.expense_amount?.amount_curr || Currency.USD) as Currency,
          id: protocol.id,
          name: protocol.name,
          note: payment?.note || '',
          optional: 'optional' in protocol ? protocol.optional : false,
          paymentScheduleId: payment?.id || null,
        } as PatientVisitRowData;
      }) || []
    );
  }

  reinitializeState() {
    void this.queryClient.invalidateQueries({
      queryKey: ['siteBudgetInvoiceables'],
      refetchType: 'active',
    });
  }

  async removeSelectedRows(cb: VoidFunction) {
    const selectedRows = this.selectedRows();

    const isAnyRowSaved = selectedRows.some((row) => !!row.paymentScheduleId);

    if (isAnyRowSaved) {
      const ref = this.overlayService.openPopup<
        ConfirmationActionModalData,
        boolean,
        ConfirmationActionModalComponent
      >({
        data: {
          hideConfirmationHint: true,
          message:
            'Are you sure you want to delete this site invoiceable? Deleting site invoiceables may impact costs that have been scheduled',
          skipKeywordConfirmation: true,
        },
        modal: ConfirmationActionModalComponent,
        settings: {
          header: 'Delete Site Invoiceables',
          primaryButton: {
            label: 'Delete',
            variant: 'negative',
          },
        },
      });

      const resp = await firstValueFrom(ref.afterClosed$);

      if (resp?.data) {
        await this.deleteSiteInvoiceablesMutation.mutateAsync({
          patientProtocolIds: selectedRows.map(({ id }) => id),
        });
        this.gridAPI()?.applyTransactionAsync({ remove: selectedRows });
        this.reinitializeState();
        cb();
      }

      return;
    }

    this.gridAPI()?.applyTransactionAsync({ remove: selectedRows });
  }

  setFirstEditableCell() {
    this.gridAPI()?.startEditingCell({
      colKey: this.isSiteInvoiceablesSelected() ? 'name' : 'amount',
      rowIndex: 0,
    });
  }

  setMostRecentSiteInvoiceablesColumnState() {
    if (this.isSiteInvoiceablesSelected()) {
      this.gridAPI()?.applyColumnState({
        state: this.siteInvoiceablesColumnState,
      });
    }
  }

  setSelectedRows(row: GridRowData, isSelected: boolean) {
    if (isSelected) {
      this.selectedRows.update((prev) => [...prev, row]);
    } else {
      this.selectedRows.update((prev) => prev.filter((selectedRow) => selectedRow.id !== row.id));
    }
  }

  validateGridData(showError: boolean = false) {
    const requred = validateRequiredField<GridRowData>(
      this.getGridData(),
      'name',
      'missingNameError',
      this.gridAPI()!
    );

    if (!requred && showError) {
      this.overlayService.error('Description is required');

      return false;
    }

    const unique = validateUniqueField<GridRowData>(
      this.getGridData(),
      'name',
      'duplicateNameError',
      this.gridAPI()!
    );

    if (!unique && showError) {
      this.overlayService.error('Description must be unique');

      return false;
    }

    return true;
  }
}
